/* ============================================================
   Somber — workshop booking hold
   Workshop tickets are added to the cart with a hidden
   `_hold_expires` line property (epoch ms, set by
   session-calendar-somber.js). This script runs on every page:
   it counts down any [data-hold-expires] element (cart page,
   cart notification) and removes expired tickets from the cart.

   Shopify only reserves stock when an order is placed, so this
   releases a forgotten cart; it can't stop another customer
   buying the same spot inside the hold window. Checkout still
   refuses a session once its spots are sold out.
   ============================================================ */

(() => {
  if (window.somberBookingHold) return;

  const NOTICE_KEY = 'somber-hold-expired';
  const script = document.currentScript;
  const holds = new Map(); // line key -> expiry (ms)
  const released = new Set(); // line keys already removed this page view
  let expiring = false;

  const holdsFromCart = (cart) => {
    holds.clear();
    (cart.items || []).forEach((item) => {
      const expires = parseInt(item.properties?._hold_expires, 10);
      if (expires) holds.set(item.key, expires);
    });
  };

  const refreshHolds = () =>
    fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        holdsFromCart(cart);
        tick();
      })
      .catch(() => {});

  const format = (ms) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  };

  const tick = () => {
    const now = Date.now();

    document.querySelectorAll('[data-hold-expires]').forEach((el) => {
      const expires = parseInt(el.dataset.holdExpires, 10);
      if (!expires) return;
      const remaining = expires - now;
      const out = el.querySelector('[data-hold-remaining]');
      if (out) out.textContent = format(remaining);
      el.hidden = false;
      el.classList.toggle('is-ending', remaining <= 120000);
      // Holds rendered on the page count too, in case the cart fetch hasn't landed yet.
      const key = el.dataset.lineKey;
      if (key && !holds.has(key) && !released.has(key)) holds.set(key, expires);
    });

    const expired = [...holds].filter(([, expires]) => expires <= now).map(([key]) => key);
    if (expired.length) expire(expired);
  };

  const expire = (keys) => {
    if (expiring) return;
    expiring = true;
    const updates = Object.fromEntries(keys.map((key) => [key, 0]));

    fetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: ['cart-icon-bubble'], sections_url: window.location.pathname }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`cart update ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        keys.forEach((key) => {
          holds.delete(key);
          released.add(key);
        });

        // The cart page's line items are server-rendered: reload and show the notice there.
        if (document.querySelector('cart-items')) {
          try {
            sessionStorage.setItem(NOTICE_KEY, '1');
          } catch (e) {}
          window.location.reload();
          return;
        }

        const bubbleHtml = cart.sections?.['cart-icon-bubble'];
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubbleHtml && bubble) {
          const section = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
          if (section) bubble.innerHTML = section.innerHTML;
        }
        document.querySelectorAll('[data-hold-expires]').forEach((el) => {
          if (keys.includes(el.dataset.lineKey)) el.closest('.cart-item')?.remove();
        });
        holdsFromCart(cart);
        showNotice();
      })
      .catch(() => {
        // Line keys may be stale (cart changed in another tab): re-sync instead of retrying every second.
        keys.forEach((key) => released.add(key));
        holds.clear();
        return refreshHolds();
      })
      .finally(() => {
        expiring = false;
      });
  };

  const showNotice = () => {
    const notice = document.createElement('div');
    notice.className = 'booking-hold-notice';
    notice.setAttribute('role', 'status');
    notice.innerHTML = `
      <p><strong>Your held spot has been released.</strong>
      Checkout wasn't completed in time, so the spot is open to others again. Choose your session again to book.</p>
      <button type="button" class="booking-hold-notice__close" aria-label="Close">&times;</button>`;
    notice.querySelector('button').addEventListener('click', () => notice.remove());
    document.body.appendChild(notice);
    setTimeout(() => notice.remove(), 12000);
  };

  const start = () => {
    try {
      if (sessionStorage.getItem(NOTICE_KEY)) {
        sessionStorage.removeItem(NOTICE_KEY);
        showNotice();
      }
    } catch (e) {}

    if (parseInt(script?.dataset.cartCount, 10) > 0) refreshHolds();
    setInterval(tick, 1000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') tick();
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => refreshHolds());
    }
  };

  window.somberBookingHold = { refresh: refreshHolds };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
